import { SearchRequestBody } from '@modules/search/hooks/useSearchProfiles'
import { AnyProfile, Company, HistoryProfileEvent, ProfileType } from '@modules/search/types'
import apiClient from '@shared/apiClient'

import { ImportProspect, ImportProspectsRequestTypes, ImportProspectsStatus } from '../components/ImportProspectsPopup/types'
import {
  ApiFilterItem,
  CompanyParticipant,
  ShortListCustomField,
  Shortlist,
  ShortlistCategoriesConfigProps,
  ShortlistFilterField,
  ShortlistItemProfile,
  ShortlistProfile,
  ShortlistProfileDataResponse,
  ShotlistDownload,
  Tag,
} from '../types'
import { getApiClient } from './apiClient'

export const fetchUserShortlistsFn = async (): Promise<Shortlist[]> => {
  const response = await getApiClient().get('/')
  return response.data
}

export const fetchShortlistPageData = async ({
  id,
  from,
  size,
  sort,
  companyIds,
  searchQuery,
  customFieldSortId,
  filters,
}: {
  id?: string | number
  from: number
  size: number
  sort: Record<string, 'asc' | 'desc'>
  companyIds?: number[]
  searchQuery?: string
  customFieldSortId?: number
  filters: ApiFilterItem<ShortlistFilterField>[]
}): Promise<ShortlistProfileDataResponse> => {
  const response = await getApiClient().post(
    `/${id}/profiles`,
    JSON.stringify({ from, size, sort, companyIds, searchQuery, customFieldName: customFieldSortId?.toString(), filters }),
  )
  return response.data
}

export const updateCategoriesConfigFn = async ({
  id,
  config,
}: {
  id: Shortlist['id']
  config: ShortlistCategoriesConfigProps
}): Promise<Shortlist> => {
  const response = await getApiClient().put(`/${id}/configuration`, JSON.stringify(config))

  return response.data
}

export const createShortlistFn = async ({ name }: { name: Shortlist['name'] }): Promise<Shortlist> => {
  const response = await getApiClient().post('/', JSON.stringify({ name: name?.trim() }))

  return response.data
}

export const deleteShortlistFn = async ({ id }: { id: Shortlist['id'] }): Promise<Shortlist> => {
  const response = await getApiClient().delete(`/${id}`)

  return response.data
}

export const downloadShortlistFn = async ({ id, date }: { id?: Shortlist['id']; date: string }): Promise<void> => {
  const response = await getApiClient().get(`/${id}/download?date=${date}`)

  return response.data
}

export const getDownloadShortlisStatustFn = async ({ id }: { id?: Shortlist['id'] }): Promise<{ download: ShotlistDownload }> => {
  const response = await getApiClient().get(`/${id}/download/status`)

  return response.data
}

export interface AttachProfileItem {
  searchDocId: ShortlistProfile['searchDocId']
  profileType: ProfileType
  profileData?: AnyProfile
  events?: HistoryProfileEvent[]
  duplicate_ids?: string[]
}

export const updateShortlistProfiles = async ({
  profiles,
  addToShortlistIds,
  removeFromShortlistIds,
}: {
  profiles: AttachProfileItem[]
  addToShortlistIds?: Array<Shortlist['id']>
  removeFromShortlistIds?: Array<Shortlist['id']>
}): Promise<Shortlist> => {
  const response = await getApiClient().post('/profiles', JSON.stringify({ profiles, removeFromShortlistIds, addToShortlistIds }))

  return response.data
}

export const updateShortlistProfileByQuery = async ({
  id,
  searchBody,
}: {
  id: ShortlistProfile['id']
  searchBody?: SearchRequestBody
}): Promise<{ importId: number }> => {
  const response = await getApiClient().post(`${id}/profiles/import/search`, JSON.stringify({ searchBody }))

  return response.data
}

export const updateShortlistProfileByIdFn = async ({
  id,
  data,
}: {
  id: ShortlistProfile['id']
  data: Partial<ShortlistProfile>
}): Promise<void> => {
  const response = await getApiClient().put(`/profiles/${id}/`, JSON.stringify(data))

  return response.data
}

export type UpdateManyMap = Record<
  Shortlist['id'],
  { name?: Shortlist['name']; status?: Shortlist['status']; id: Shortlist['id']; filters?: Shortlist['filters'] }
>

export const bulkShortlistUpdateFn = async (data: UpdateManyMap): Promise<void> => {
  await getApiClient().put('/', JSON.stringify(data))
}

export const moveProfileBetweenShortlistsFn = async ({
  fromShortlistId,
  toShortlistIds,
  profileIds,
  throwOnDuplicates,
  copyProspects,
}: {
  fromShortlistId: Shortlist['id']
  toShortlistIds: Shortlist['id'][]
  profileIds: ShortlistItemProfile['id'][]
  throwOnDuplicates?: boolean
  copyProspects?: boolean
}): Promise<Shortlist> => {
  let path = `${fromShortlistId}/profiles/move`

  if (throwOnDuplicates) {
    path = `${path}?throwOnDuplicates=${throwOnDuplicates}`
  }

  const response = await getApiClient().post(
    path,
    JSON.stringify({
      profileIds,
      addToShortlistIds: toShortlistIds,
      keepProfilesInOrigin: copyProspects || false,
    }),
  )

  return response.data
}

export const fetchUserTags = async ({ eventId }: { eventId: string }): Promise<Tag[]> => {
  const response = await apiClient.partneringOne('events').get(`p1-plus/${eventId}/tags`)

  return response.data?.data
}

export const fetchCompanyParticipants = async ({ eventId }: { eventId: string }): Promise<CompanyParticipant[]> => {
  const response = await apiClient.partneringOne('events').get(`p1-plus/${eventId}/participants`)
  return response.data?.data?.participants
}

export const fetchProspectsByFilters = async ({
  eventId,
  requests,
  statuses,
  tagIds,
  companyId,
  participantId,
}: {
  eventId: string
  requests: ImportProspectsRequestTypes[]
  statuses: ImportProspectsStatus[]
  tagIds: number[]
  companyId: number
  participantId?: number
}): Promise<{ result: { status: ImportProspectsStatus; companies: ImportProspect[] }[]; total: number }> => {
  const data = { companyId } as {
    companyId: number
    direction?: ImportProspectsRequestTypes[]
    status?: ImportProspectsStatus[]
    tags?: number[]
    participantId?: number
  }

  if (requests?.length) {
    data.direction = requests
  }

  if (statuses?.length) {
    data.status = statuses
  }

  if (tagIds?.length) {
    data.tags = tagIds
  }

  if (participantId) {
    data.participantId = participantId
  }

  const response = await apiClient.partneringOne('requesting').post(`p1plus/${eventId}/prospects`, JSON.stringify(data))

  return response.data?.data
}

export const submitImportProspects = async ({
  shId,
  prospectIds,
}: {
  shId: Shortlist['id']
  prospectIds: Company['id'][]
}): Promise<Shortlist['id']> => {
  const profiles = prospectIds.map(id => ({ id, type: ProfileType.company }))

  await getApiClient().post(`${shId}/profiles/import`, JSON.stringify({ profiles }))

  return shId
}

export const fetchSortableFields = async ({
  shortlistId,
}: {
  shortlistId: Shortlist['id']
}): Promise<Record<string, boolean>> => {
  const response = await getApiClient().get(`/${shortlistId}/profiles/sortable-fields`)
  return response.data
}

export const createCustomFieldFn = async ({
  shortlistId,
  customField,
}: {
  shortlistId: Shortlist['id']
  customField: ShortListCustomField
}): Promise<Shortlist> => {
  const response = await getApiClient().post(`/${shortlistId}/custom-fields`, JSON.stringify(customField))

  return response.data
}

export const editCustomFieldFn = async ({
  shortlistId,
  customField,
}: {
  shortlistId: Shortlist['id']
  customField: ShortListCustomField
}): Promise<Shortlist> => {
  const response = await getApiClient().put(`/${shortlistId}/custom-fields/${customField.id}`, JSON.stringify(customField))

  return response.data
}

export const updateShortlistById = async ({
  shortlistId,
  params,
}: {
  shortlistId: Shortlist['id']
  params: {
    name: string
    filters?: ApiFilterItem[]
    sort?: {
      field: string
      order: 'asc' | 'desc'
    }
  }
}): Promise<Shortlist> => {
  const response = await getApiClient().put(`/${shortlistId}`, JSON.stringify(params))

  return response.data
}

export const undoAddProfilesToShortlists = async ({
  shortlistId,
  importId,
}: {
  shortlistId: Shortlist['id']
  importId: string
}): Promise<Shortlist> => {
  const response = await getApiClient().post(`/${shortlistId}/profiles/import/search/undo`, JSON.stringify({ importId }))

  return response.data
}
